<template>
	<div>
		<FormKit
			type="dropdown"
			v-model="selectedFDMethod"
			:options="options"
			:label-class="{
				required: isRequired,
				'text-sm': true,
				'!font-normal': true,
			}"
			:placeholder="t('document.submission.selectFd')"
			selector-class="text-sm"
			outer-class="max-w-full"
			inner-class="min-w-[300px]"
			deselect
		/>
		<CustomFdWriteIn
			v-if="showCustomInputFields"
			v-model="customFDInfo"
			@validity-changed="updateValidity"
		/>
	</div>
</template>

<script setup lang="ts">
import CustomFdWriteIn from '@/components/documents/CompleteStep/CustomFdWriteIn.vue'
import { usePresentationLocale } from '@/i18n'
import { useSessionDocumentsStore } from '@/stores/sessionDocuments'
import type { SignerInfo, SignerSelection } from '@/types'
import { computed, ref } from 'vue'

const props = defineProps({
	planHolderId: { type: String, required: true },
})

const emit = defineEmits(['validity-changed'])

const sessionDocumentsStore = useSessionDocumentsStore()
const { t } = usePresentationLocale()

const options = [
	{
		label: t('document.submission.doNotSendToFd'),
		value: 'not-required',
	},
	{
		label: t('document.submission.writeInCustom'),
		value: 'write-in-custom',
	},
]

/**
 * TODO: PSS-1345: Get Funeral Director data from the Papi API when they are available
 * */
const availableFuneralDirectors: {
	id: string
	firstName: string
	lastName: string
	email: string
}[] = []

const emptyCustomFd = (): SignerInfo => ({
	firstName: '',
	lastName: '',
	email: '',
})

const previousFDInput = ref<SignerInfo>()

const selectedFDMethod = computed({
	get() {
		const signer = sessionDocumentsStore.selectedSigners[props.planHolderId]

		if (signer === 'not-required') {
			return 'not-required'
		}

		if (!signer) {
			return undefined
		}

		const matchedFd = availableFuneralDirectors.find(
			(fd) => fd.id === signer.id
		)
		if (matchedFd) {
			return matchedFd.id
		}

		return 'write-in-custom'
	},
	set(selectedMethod) {
		const existingSignerInfo =
			sessionDocumentsStore.selectedSigners[props.planHolderId]

		//set signer info to local ref so if they switch back we can load previous info
		if (existingSignerInfo && existingSignerInfo !== 'not-required') {
			previousFDInput.value = existingSignerInfo
		}

		let selectedSigner: SignerSelection

		if (selectedMethod === 'not-required') {
			selectedSigner = 'not-required'
			updateValidity(true)
		} else if (selectedMethod === 'write-in-custom') {
			selectedSigner = handleLoadingFDInfo(existingSignerInfo)
		} else if (selectedMethod) {
			selectedSigner = availableFuneralDirectors.find(
				(fd) => fd.id === selectedMethod
			)
			updateValidity(true)
		} else {
			selectedSigner = undefined
		}

		sessionDocumentsStore.setSelectedSigner(props.planHolderId, selectedSigner)
	},
})

// logic for how to get signer info or load empty
const handleLoadingFDInfo = (signerInfo: SignerSelection) => {
	if (signerInfo && signerInfo !== 'not-required') {
		return signerInfo
	}

	if (previousFDInput.value !== undefined) {
		return previousFDInput.value
	}

	return emptyCustomFd()
}
const showCustomInputFields = computed(
	() => selectedFDMethod.value === 'write-in-custom'
)

const customFDInfo = computed({
	get() {
		const selectedSigner =
			sessionDocumentsStore.selectedSigners[props.planHolderId]

		if (selectedSigner && selectedSigner !== 'not-required') {
			return selectedSigner
		}

		if (previousFDInput.value) {
			return previousFDInput.value
		}

		return emptyCustomFd()
	},
	set(fdInfo: SignerInfo) {
		sessionDocumentsStore.setSelectedSigner(props.planHolderId, fdInfo)
	},
})

const isRequired = sessionDocumentsStore.isStateRequiresSignature(
	props.planHolderId
)

const updateValidity = (newValue: boolean) => {
	emit('validity-changed', newValue)
}
</script>

<style scoped>
@reference "tailwindcss";
:deep(.formkit-label.required::after) {
	content: '*';
	@apply text-red-400;
	margin-left: 6px;
}
</style>
